// useAuthorPosts hook for fetching user's own posts (excluding resteems)

import { useInfiniteQuery } from '@tanstack/react-query'
import { getPostsByAuthor } from '@/services/feeds/feedsClient'
import { transformFeedResponse } from '@/services/feeds/transformer'
import { queryKeys } from '@/services/queryClient'
import { Post } from '@/types/post'

interface UseAuthorPostsOptions {
  username: string
  limit?: number
  enabled?: boolean
}

export function useAuthorPosts({ username, limit = 20, enabled = true }: UseAuthorPostsOptions) {
  return useInfiniteQuery({
    queryKey: queryKeys.authorPosts(username),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getPostsByAuthor(username, {
        offset: pageParam,
        limit,
        bodyLength: 250,
      })

      const rows = response?.result?.rows || []
      const posts = transformFeedResponse(rows)

      return {
        posts,
        nextOffset: pageParam + limit,
        hasMore: posts.length === limit,
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextOffset : undefined
    },
    initialPageParam: 0,
    enabled: enabled && !!username,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function getAllAuthorPosts(
  data: { pages: { posts: Post[] }[] } | undefined
): Post[] {
  if (!data) return []
  return data.pages.flatMap((page) => page.posts)
}
